import { motion } from "framer-motion";
import { BadgePercent, Check, Clock, Maximize2, Sparkles } from "lucide-react";
import { effectivePrice, isOnSale, saleEndsIn } from "@/lib/pricing";

export const PackageCard = ({ pkg, index = 0, onPurchase, onView, compact = false }) => {
  const sale = isOnSale(pkg);
  const endsIn = saleEndsIn(pkg);

  return (
    <motion.article
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`package-card-${pkg.id}`}
      onClick={compact && onView ? () => onView(pkg) : undefined}
      className={`card-lift group relative flex flex-col rounded-2xl border border-white/10 bg-[#0D0B12]/80 overflow-hidden ${compact ? "cursor-pointer" : ""}`}
    >
      <div className="relative aspect-[16/9] overflow-hidden">
        <img
          src={pkg.image_url}
          alt={pkg.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0B12] via-[#0D0B12]/30 to-transparent" />
        {pkg.featured && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#8B5CF6] text-white text-[10px] font-bold tracking-[0.15em]">
            <Sparkles className="w-3 h-3" /> FEATURED
          </span>
        )}
        {(pkg.deal_roles?.length > 0 || pkg.deal_users?.length > 0) && (
          <span className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
            {pkg.deal_roles?.map((r) => (
              <span
                key={r}
                data-testid={`package-deal-badge-${pkg.id}-${r}`}
                className={`px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.15em] ${
                  r === "staff" ? "bg-emerald-400 text-black" : "bg-amber-400 text-black"
                }`}
              >
                {r.toUpperCase()} DEAL
              </span>
            ))}
            {pkg.deal_users?.length > 0 && (
              <span
                data-testid={`package-deal-badge-${pkg.id}-private`}
                className="px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.15em] bg-[#8B5CF6] text-white"
              >
                PRIVATE DEAL
              </span>
            )}
          </span>
        )}
        {sale && (
          <span
            data-testid={`package-sale-badge-${pkg.id}`}
            className={`absolute top-3 ${pkg.featured ? "left-28" : "left-3"} inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400 text-black text-[10px] font-bold tracking-[0.15em]`}
          >
            <BadgePercent className="w-3 h-3" /> SALE
          </span>
        )}
        {endsIn && (
          <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur border border-amber-400/40 text-amber-300 text-[10px] font-bold tracking-[0.1em]" data-testid={`package-sale-timer-${pkg.id}`}>
            <Clock className="w-3 h-3" /> ENDS IN {endsIn.toUpperCase()}
          </span>
        )}
        {compact && !pkg.deal_roles?.length && !pkg.deal_users?.length && (
          <span className="absolute top-3 right-3 p-2 rounded-lg bg-black/50 backdrop-blur border border-white/10 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <Maximize2 className="w-3.5 h-3.5" />
          </span>
        )}
        <div className="absolute bottom-3 right-4 text-right">
          {sale && (
            <span className="block text-xs text-slate-400 line-through" data-testid={`package-original-price-${pkg.id}`}>
              ${pkg.price.toFixed(2)}
            </span>
          )}
          <span className={`font-display text-2xl drop-shadow-[0_0_12px_rgba(139,92,246,0.6)] ${sale ? "text-amber-300" : "text-white"}`} data-testid={`package-price-${pkg.id}`}>
            ${effectivePrice(pkg).toFixed(2)}{pkg.recurring && <span className="text-xs font-sans font-normal text-slate-500 tracking-wide">/{pkg.recurring === "month" ? "mo" : pkg.recurring}</span>}
          </span>
        </div>
      </div>

      <div className="flex flex-col flex-1 p-6">
        <h3 className="font-display text-2xl tracking-tight text-white" data-testid={`package-name-${pkg.id}`}>{pkg.name}</h3>
        {!compact && <p className="mt-2 text-sm text-slate-400 leading-relaxed">{pkg.description}</p>}

        {!compact && pkg.perks?.length > 0 && (
          <ul className="mt-4 space-y-2">
            {pkg.perks.map((perk) => (
              <li key={perk} className="flex items-start gap-2 text-sm text-slate-300">
                <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#8B5CF6]" strokeWidth={3} />
                {perk}
              </li>
            ))}
          </ul>
        )}

        {compact && (
          <p className="mt-1 text-xs text-slate-600">Click to view details</p>
        )}

        <button
          onClick={(e) => { e.stopPropagation(); onPurchase(pkg); }}
          data-testid={`package-purchase-button-${pkg.id}`}
          className="mt-6 w-full py-3 rounded-xl bg-[#8B5CF6]/15 border border-[#8B5CF6]/40 text-[#C4B5FD] text-xs font-bold tracking-[0.2em] hover:bg-[#8B5CF6] hover:text-white hover:border-[#8B5CF6] transition-[background-color,color,border-color] duration-300"
        >
          PURCHASE
        </button>
      </div>
    </motion.article>
  );
};
